import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { useTableOverlayScrollbars } from "@/hooks/use-overlay-scrollbars.hook.ts";
import { ApiPaginationMetadata } from "@skailer-com/uikit/core";
import {
  Box,
  Button,
  EmptyStateBase,
  FontAwesomeIcon,
  PaginationBlock,
  Stack,
  Table,
  type TableScrollLayout,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { type ReactNode, type RefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface HistoryTableProps<TData extends object> {
  currentItem?: TData;
  historyItems?: TData[];

  columns: ColumnDef<TData, unknown>[];
  canViewHistory: boolean;
  defaultVisibleRecords?: number;
  stickyLastColumn?: boolean;
  renderRowAction?: (row: Row<TData>) => ReactNode;
  onRowClick?: (row: Row<TData>) => void;

  isLoading: boolean;
  isFetching?: boolean;

  pagination?: {
    metadata?: ApiPaginationMetadata;
    currentPage: number;
    onPageChange: (page: number) => void;
  };

  emptyStateTitle?: string;
  notFoundComponent?: React.ReactNode;

  scrollLayout?: TableScrollLayout;
  scrollBoundaryRef?: RefObject<HTMLElement | null>;
}

export function HistoryTable<TData extends object>({
  currentItem,
  historyItems,
  columns,
  canViewHistory,
  defaultVisibleRecords = 4,
  isLoading,
  isFetching = false,
  pagination,
  emptyStateTitle,
  notFoundComponent = <EmptyStateNothingFound />,
  scrollLayout,
  scrollBoundaryRef,
  stickyLastColumn = true,
  renderRowAction,
  onRowClick,
}: HistoryTableProps<TData>) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const overlayScrollbarProps = useTableOverlayScrollbars();

  const displayData = useMemo(() => {
    if (!canViewHistory) {
      return currentItem ? [currentItem] : [];
    }

    const items = historyItems || [];

    if (showAll || items.length <= defaultVisibleRecords) {
      return items;
    }

    return items.slice(0, defaultVisibleRecords);
  }, [historyItems, currentItem, showAll, canViewHistory, defaultVisibleRecords]);

  const remainingRecords = useMemo(() => {
    if (!canViewHistory) return 0;
    const total = historyItems?.length || 0;
    return Math.max(0, total - defaultVisibleRecords);
  }, [historyItems, defaultVisibleRecords, canViewHistory]);

  const shouldShowMoreButton = useMemo(() => {
    return canViewHistory && !showAll && remainingRecords > 0;
  }, [canViewHistory, showAll, remainingRecords]);

  const handleShowAll = () => setShowAll(true);

  return (
    <Box>
      <Table<TData>
        {...overlayScrollbarProps}
        data={displayData}
        columns={columns}
        isFetching={isFetching}
        showSkeleton={isLoading}
        notFoundComponent={notFoundComponent}
        renderRowAction={renderRowAction}
        onRowClick={onRowClick}
        stickyLastColumn={stickyLastColumn}
        stickyFirstColumn={true}
        scrollLayout={scrollLayout}
        scrollBoundaryRef={scrollBoundaryRef}
        emptyStateComponent={
          <EmptyStateBase variant="plain" title={emptyStateTitle || t("common:no_data")} />
        }
      />

      {shouldShowMoreButton && (
        <Stack className="pt-(--gap-md)" direction="horizontal">
          <Button
            onClick={handleShowAll}
            variant="secondary"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
          >
            {t("common:show_more_template", { count: remainingRecords })}
          </Button>
        </Stack>
      )}

      {showAll &&
        pagination &&
        pagination.metadata &&
        (pagination.metadata?.total_pages ?? 0) > 1 && (
          <PaginationBlock metadata={pagination.metadata} onPageChange={pagination.onPageChange} />
        )}
    </Box>
  );
}
